import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreatePaymentOrderMutation, useVerifyPaymentMutation, useSendOtpMutation, useVerifyOtpMutation } from "../redux/api/roomBookingApi";
import { useLazyGetRegisteredUserByPhoneQuery } from "../redux/api/roomApi";
import useCustomModal from '../hooks/useCustomModal';
import { Country, State, City } from 'country-state-city';
import './SevaBookingSummary.css';

const SevaBookingSummary = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { cartItems: initialCartItems = [], sevaDate: initialSevaDate, isDonation = false, is_seva = false, is_guru_seva = false } = location.state || {}; // Expect cartItems and sevaDate passed in state
    const [cartItems, setCartItems] = useState(initialCartItems);
    const [sevaDate, setSevaDate] = useState(initialSevaDate || new Date().toISOString().split('T')[0]);

    // Payment Mutations
    // Payment Mutations
    const [createOrder] = useCreatePaymentOrderMutation();
    const [verifyPayment] = useVerifyPaymentMutation();
    const [sendOtp] = useSendOtpMutation();
    const [verifyOtp] = useVerifyOtpMutation();

    // Easebuzz Payment Flow
    const handleEasebuzzPayment = async (amount, booking_id, customer) => {
        try {
            const orderPayload = {
                booking_id: booking_id,
                amount: amount,
                customer_id: customer.phone,
                customer_phone: customer.phone,
                customer_name: customer.name,
                customer_email: customer.email,
                customer_address: customer.address,
                order_items: cartItems,
                seva_date: sevaDate,
                is_donation: isDonation,
                is_seva: is_seva,
                is_guru_seva: is_guru_seva
            };

            const orderRes = await createOrder(orderPayload).unwrap();

            if (orderRes.access_key) {
                const env = orderRes.env || 'test'; 
                const paymentUrl = `https://${env === 'test' ? 'testpay' : 'pay'}.easebuzz.in/pay/${orderRes.access_key}`;
                window.location.href = paymentUrl;
            } else {
                showModal("Failed to initiate payment with Easebuzz", 'error');
            }
        } catch (err) {
            console.error(err);
            showModal(err?.data?.message || "❌ Payment initialization failed", 'error');
        }
    };

    const [mobile, setMobile] = useState('');
    const [isFormExpanded, setIsFormExpanded] = useState(false);
    const [isTestingOtpEnabled, setIsTestingOtpEnabled] = useState(false);

    // Fetch settings for testing mode
    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/settings`)
            .then(res => res.json())
            .then(data => {
                if (data && data.status === 'success' && data.data) {
                    setIsTestingOtpEnabled(data.data.is_testing_otp_enabled === true);
                }
            })
            .catch(err => console.error("Error fetching settings:", err));
    }, []);

    // Auto-fill phone if testing enabled
    useEffect(() => {
        if (isTestingOtpEnabled && !mobile) {
            setMobile('9845098450');
        }
    }, [isTestingOtpEnabled, mobile]);

    // OTP State
    const [otp, setOtp] = useState('');
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [isOtpVerified, setIsOtpVerified] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [tempUserData, setTempUserData] = useState(null);

    // Basic Form State
    const [formData, setFormData] = useState({
        _id: null,
        firstName: '',
        email: '',
        address: '',
        city: '',
        state: '',
        country: 'India', // Default to India name (Room Booking uses names in formData)
        pincode: '',
    });

    // Validation & Location Sync State
    const [errors, setErrors] = useState({});
    const [selectedCountry, setSelectedCountry] = useState("IN"); // Default ISO
    const [selectedState, setSelectedState] = useState("");

    // Sync selectedCountry/State ISO codes when formData.country/state changes
    useEffect(() => {
        if (formData.country) {
            const countryObj = Country.getAllCountries().find(c => c.name === formData.country);
            if (countryObj) {
                setSelectedCountry(countryObj.isoCode);
                if (formData.state) {
                    const stateObj = State.getStatesOfCountry(countryObj.isoCode).find(s => s.name === formData.state);
                    if (stateObj) setSelectedState(stateObj.isoCode);
                }
            }
        }
    }, [formData.country, formData.state]);

    // Initialize Custom UI Modals globally
    const { showModal, CustomModalComponent } = useCustomModal();

    // Calculate totals
    const subTotal = cartItems.reduce((acc, item) => acc + Number(item.seva_amount), 0);
    const convenienceFee = 0; // Using 0 for now as per plan, can be 0.05 * subTotal
    const totalAmount = subTotal + convenienceFee;

    useEffect(() => {
        // If no items, redirect back to booking
        if (!cartItems || cartItems.length === 0) {
            navigate('/online-seva-booking'); // Adjust route if needed
        }
    }, [cartItems, navigate]);

    const [userFound, setUserFound] = useState(false);

    // Unified User Fetching Hook (Matches RoomBooking.jsx)
    const [trigger, { data: userData }] = useLazyGetRegisteredUserByPhoneQuery();

    useEffect(() => {
        if (userData?.data) {
            const u = userData.data;
            setFormData(prev => ({
                ...prev,
                _id: u._id,
                firstName: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.full_name || '',
                email: u.email_id || '',
                address: u.address || '',
                city: u.town || '',
                state: u.state || '',
                country: u.country || 'India',
                pincode: u.postal_code || ''
            }));
            setUserFound(true);
        }
    }, [userData]);

    const handleRemoveItem = (indexToRemove) => {
        const updatedCart = cartItems.filter((_, index) => index !== indexToRemove);
        setCartItems(updatedCart);
        // Optional: Update session storage to reflect removal? 
        // For now, we only update current view. The "Back" button will still reload from session storage original state, 
        // effectively "Resetting" the removal, which is acceptable behavior ("Reset Selection").
    };

    const handleMobileSubmit = async (e) => {
        e.preventDefault();
        if (mobile.length === 10) {
            // 1. Try to fetch user details first (using Hook)
            await trigger(mobile);
            // Note: The Effect above will handle the success case.

            // 2. Send OTP
            try {
                const otpRes = await sendOtp({ phone: mobile }).unwrap();
                if (otpRes.status === 'success') {
                    if (process.env.REACT_APP_ENABLE_SANDBOX_OTP === 'true' || isTestingOtpEnabled) {
                        showModal(`OTP Sent to ${mobile}` + (otpRes.otp ? ` (Testing: ${otpRes.otp})` : ''), 'success');
                        if (isTestingOtpEnabled && otpRes.otp) {
                            setOtp(otpRes.otp);
                        }
                    } else {
                        showModal(`OTP Sent to ${mobile}`, 'success');
                    }
                    setOtpSent(true);
                    setShowOtpInput(true);
                } else {
                    showModal("Failed to send OTP", 'error');
                }
            } catch (otpErr) {
                console.error("OTP Send Error", otpErr);
                showModal("Error sending OTP. Please try again.", 'error');
            }
        } else {
            showModal("Please enter a valid 10-digit mobile number", 'warning');
        }
    };

    const handleVerifyOtpAction = async (e) => {
        e.preventDefault();
        try {
            const res = await verifyOtp({ phone: mobile, otp }).unwrap();
            if (res.status === 'success' && res.verified) {
                showModal("OTP Verified Successfully!", 'success');
                setIsOtpVerified(true);
                setShowOtpInput(false);
                setIsFormExpanded(true); // Always expand form, no pre-fill
            } else {
                showModal("Invalid OTP", 'error');
            }
        } catch (err) {
            console.error("OTP Verification Error", err);
            showModal("Error verifying OTP", 'error');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Name Validation (On Type)
        if (name === 'firstName') {
            if (/[^a-zA-Z\s]/.test(value)) {
                setErrors(prev => ({ ...prev, firstName: "Numbers/Special characters not allowed" }));
                return; // Block input
            } else if (errors.firstName) {
                setErrors(prev => ({ ...prev, firstName: "" }));
            }
        }

        // Pincode Validation (On Type)
        if (name === 'pincode') {
            if (selectedCountry === 'IN' || selectedCountry === '') {
                if (/[^0-9]/.test(value)) return;
                if (value.length > 6) return;
            } else {
                if (/[^a-zA-Z0-9]/.test(value)) return;
                if (value.length > 10) return;
            }
        }

        setFormData(prev => ({ ...prev, [name]: value }));

        if (name !== 'firstName' && errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleCountryChange = (e) => {
        const isoCode = e.target.value;
        const countryData = Country.getCountryByCode(isoCode);
        setSelectedCountry(isoCode);
        setSelectedState("");
        setFormData(prev => ({
            ...prev,
            country: countryData ? countryData.name : "",
            state: "",
            city: ""
        }));
    };

    const handleStateChange = (e) => {
        const isoCode = e.target.value;
        const stateData = State.getStateByCodeAndCountry(isoCode, selectedCountry);
        setSelectedState(isoCode);
        setFormData(prev => ({
            ...prev,
            state: stateData ? stateData.name : "",
            city: ""
        }));
    };

    const validateField = (name, value) => {
        let error = '';
        if (!value || value.toString().trim() === '') {
            if (name === 'firstName') error = "Full Name is required";
            if (name === 'email') error = "Email is required";
            if (name === 'address') error = "Address is required";
            if (name === 'city') error = "City is required";
            if (name === 'pincode') error = selectedCountry === 'IN' ? "Pincode is required" : "Area Code is required";
            if (name === 'country') error = "Country is required";
            if (name === 'state') error = "State is required";
        } else {
            if (name === 'email') {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) error = "Invalid Email Address";
            }
            if (name === 'pincode') {
                if (selectedCountry === 'IN' || selectedCountry === '') {
                    if (!/^\d{6}$/.test(value)) error = "Pincode must be exactly 6 digits";
                } else {
                    if (value.length > 10) error = "Max 10 characters allowed";
                    else if (!/^[a-zA-Z0-9]+$/.test(value)) error = "Alphanumeric only";
                }
            }
        }
        setErrors(prev => ({ ...prev, [name]: error }));
    };

    const handleBlur = (e) => validateField(e.target.name, e.target.value);

    const handleProceed = async (e) => {
        if (e) e.preventDefault();

        // --- JS Validation Logic ---
        const validationErrors = {};

        // Full Name
        if (!formData.firstName || formData.firstName.trim().length < 3) validationErrors.firstName = "Full Name must be at least 3 characters";

        // Email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email || !emailRegex.test(formData.email)) validationErrors.email = "Invalid Email Address";

        // Pincode
        if (selectedCountry === 'IN' || selectedCountry === '') {
            if (!formData.pincode || !/^\d{6}$/.test(formData.pincode)) validationErrors.pincode = "Pincode must be 6 digits";
        } else {
            if (!formData.pincode) validationErrors.pincode = "Required";
            else if (formData.pincode.length > 10) validationErrors.pincode = "Max 10 characters";
            else if (!/^[a-zA-Z0-9]+$/.test(formData.pincode)) validationErrors.pincode = "Alphanumeric only";
        }

        // Location Info Empty checks
        if (!formData.address) validationErrors.address = "Address is required";
        if (!formData.city) validationErrors.city = "City is required";
        if (!formData.state) validationErrors.state = "State is required";

        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            return; // Stop if validation fails
        }

        // Enforce OTP Verification
        if (!isOtpVerified) {
            if (!otpSent) {
                showModal("Please verify mobile number first.", 'warning');
                return;
            }
            showModal("Please enter and verify OTP.", 'warning');
            return;
        }

        // --- Redirect to Easebuzz ---
        const bookingId = isDonation
            ? `DON-${Date.now()}`
            : is_guru_seva
            ? `GURU-${Date.now()}`
            : `SEVA-${Date.now()}`;
        handleEasebuzzPayment(totalAmount, bookingId, {
            phone: mobile,
            name: formData.firstName,
            email: formData.email,
            address: formData.address
        });
    };

    return (
        <div className="seva-summary-page position-relative">

            <CustomModalComponent />
            <div className="invoice-header text-center">
                <h2>Seva Booking Summary</h2>
            </div>

            <div className="container">
                <div className="summary-container">
                    {/* Left Column: Summary */}
                    <div className="summary-left">
                        <h4 className="summary-title">Booking Details</h4>

                        <div className="text-muted mb-3">
                            <strong>Seva Date:</strong> {new Date(sevaDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>

                        <ul className="summary-list">
                            {cartItems.map((item, index) => (
                                <li key={index} className="summary-item">
                                    {/* Display category/god name if available */}
                                    <span className="summary-item-name">
                                        {item.god_name && <small className="d-block text-muted">{item.god_name}</small>}
                                        {item.seva_name}
                                    </span>
                                    <span className="summary-item-amount">
                                        ₹{item.seva_amount}
                                        <button
                                            onClick={() => handleRemoveItem(index)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#dc3545',
                                                marginLeft: '10px',
                                                cursor: 'pointer',
                                                padding: '0 5px'
                                            }}
                                            title="Remove Item"
                                        >
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <div className="convenience-fee">
                            <span>Convenience Fee</span>
                            <span>₹{convenienceFee}</span>
                        </div>

                        <div className="total-amount-box">
                            <div className="total-label">Total Amount</div>
                            <div className="total-value">₹ {totalAmount}/-</div>
                        </div>

                        <button className="btn-reset" onClick={() => navigate(-1)}>
                            &larr; Reset Selection
                        </button>
                    </div>

                    {/* Right Column: Personal Details */}
                    <div className="summary-right">
                        <h4 className="summary-title">Personal Details</h4>

                        {!isFormExpanded ? (
                            <form onSubmit={!showOtpInput ? handleMobileSubmit : handleVerifyOtpAction}>
                                <div className="form-group">
                                    <label className="form-label">Mobile Number</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter 10-digit Mobile Number"
                                        value={mobile}
                                        onChange={(e) => {
                                            if (/^\d*$/.test(e.target.value)) {
                                                setMobile(e.target.value);
                                            }
                                        }}
                                        maxLength={10}
                                        required
                                        disabled={showOtpInput}
                                    />
                                </div>

                                {showOtpInput && (
                                    <div className="form-group">
                                        <label className="form-label">Enter OTP</label>
                                        <div className="d-flex gap-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                // placeholder="OTP"
                                                placeholder="Enter the 6-digit OTP"
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                                maxLength={6}
                                            />
                                            <button type="submit" className="btn btn-success" style={{ minWidth: '100px' }}>Verify</button>
                                        </div>
                                    </div>
                                )}

                                {!showOtpInput && (
                                    <button type="submit" className="btn-proceed" style={{ background: '#333' }}>Next</button>
                                )}
                            </form>
                        ) : (
                            <form className="expanded-form" onSubmit={handleProceed}>
                                <div className="form-row">
                                    <div className="form-col form-group">
                                        <label className="form-label">Mobile Number</label>
                                        <input type="text" className="form-control" value={mobile} readOnly disabled />
                                        {isOtpVerified && <small className="text-success">Verified</small>}
                                    </div>
                                    <div className="form-col form-group">
                                        <label className="form-label">Email ID</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-control"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            required
                                        />
                                        {errors.email && <small className="text-danger">{errors.email}</small>}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Full Name</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        className="form-control"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        required
                                    />
                                    {errors.firstName && <small className="text-danger">{errors.firstName}</small>}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Address</label>
                                    <textarea
                                        name="address"
                                        className="form-control"
                                        rows="2"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        required
                                    ></textarea>
                                    {errors.address && <small className="text-danger">{errors.address}</small>}
                                </div>

                                <div className="form-row">
                                    <div className="form-col form-group">
                                        <label className="form-label">Country</label>
                                        <select
                                            name="country"
                                            className="form-control"
                                            value={selectedCountry}
                                            onChange={handleCountryChange}
                                            required
                                        >
                                            <option value="">Select Country</option>
                                            {Country.getAllCountries().map(c => (
                                                <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-col form-group">
                                        <label className="form-label">State</label>
                                        {selectedCountry && State.getStatesOfCountry(selectedCountry).length > 0 ? (
                                            <select
                                                name="state"
                                                className="form-control"
                                                value={selectedState}
                                                onChange={handleStateChange}
                                                required
                                            >
                                                <option value="">Select State</option>
                                                {State.getStatesOfCountry(selectedCountry).map(s => (
                                                    <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                name="state"
                                                className="form-control"
                                                value={formData.state}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                required
                                                disabled={!selectedCountry}
                                            />
                                        )}
                                        {errors.state && <small className="text-danger">{errors.state}</small>}
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-col form-group">
                                        <label className="form-label">City/Town</label>
                                        {selectedState && City.getCitiesOfState(selectedCountry, selectedState).length > 0 ? (
                                            <select
                                                name="city"
                                                className="form-control"
                                                value={formData.city}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select City</option>
                                                {City.getCitiesOfState(selectedCountry, selectedState).map((city) => (
                                                    <option key={city.name} value={city.name}>{city.name}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                name="city"
                                                className="form-control"
                                                value={formData.city}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                required
                                                disabled={!formData.state}
                                            />
                                        )}
                                        {errors.city && <small className="text-danger">{errors.city}</small>}
                                    </div>
                                    <div className="form-col form-group">
                                        <label className="form-label">{selectedCountry === 'IN' || selectedCountry === '' ? 'Pincode' : 'Area Code'}</label>
                                        <input
                                            type="text"
                                            name="pincode"
                                            className="form-control"
                                            value={formData.pincode}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            required
                                        />
                                        {errors.pincode && <small className="text-danger">{errors.pincode}</small>}
                                    </div>
                                </div>

                                {showOtpInput && !userFound && (
                                    <div className="form-group bg-light p-3 rounded">
                                        <label className="form-label">Verify Mobile to Proceed</label>
                                        <div className="d-flex gap-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter OTP sent to mobile"
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                                maxLength={6}
                                            />
                                            <button type="button" onClick={handleVerifyOtpAction} className="btn btn-success">Verify</button>
                                        </div>
                                    </div>
                                )}

                                <button type="submit" className="btn-proceed">Proceed to Pay</button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SevaBookingSummary;
